import { Chip } from '@mui/material';
import { concat, find, transform } from 'lodash';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UseQueryResult } from 'react-query';

import {
  CopyTextId,
  CrudPage,
  DataGridColumnDefinition,
  DateLabel,
  FormikSelectOption,
  Money,
  StatusLabel,
} from 'components';
import { AppConfigArea, AppConfigKey, FilterDefinitionType } from 'enums';
import {
  useAppConfigValue,
  useQueryFilters,
  useUser,
  useUserContext,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  FilterDefinition,
  PaginatedData,
  InternalTransfer,
  InternalTransferStatus,
  InternalTransferFilters,
  Shop,
} from 'types';
import { filtersUtils, formatUtils } from 'utils';

type Props = {
  queryResult: UseQueryResult<PaginatedData<InternalTransfer>>;
  queryResultShops?: UseQueryResult<Shop[]>;
  hideTitle?: boolean;
  additionalColumns?: DataGridColumnDefinition<InternalTransfer>[];
  additionalFilters?: FilterDefinition<Partial<InternalTransferFilters>>[];
};

export const InternalTransfers: React.FC<Props> = ({
  queryResult,
  queryResultShops,
  additionalColumns = [],
  additionalFilters = [],
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.internal_transfers',
  });
  const { filters } = useQueryFilters<InternalTransferFilters>();
  const { p2pProviders } = useUserContext();
  const { isAdminOrTechOperator } = useUser();

  const categoriesConfigQueryResult = useAppConfigValue({
    area: AppConfigArea.Platform,
    key: AppConfigKey.InternalTransferCategories,
  });

  const categoryOptions = useMemo(
    () =>
      transform(
        categoriesConfigQueryResult?.data || {},
        (acc, label, value) => acc.push({ label: label as string, value }),
        [] as FormikSelectOption[],
      ),
    [categoriesConfigQueryResult?.data],
  );

  const getCategoryLabel = useCallback(
    (category: string) =>
      (find(categoryOptions, { value: category })?.label as string) || category,
    [categoryOptions],
  );

  const commonFilters: FilterDefinition<InternalTransferFilters>[] = useMemo(
    () => [
      {
        label: t('fields.category'),
        name: 'category',
        type: FilterDefinitionType.Select,
        getDisplayName: getCategoryLabel,
        options: categoryOptions,
        hidden: !isAdminOrTechOperator,
      },
      {
        label: t('fields.code'),
        name: 'id',
        type: FilterDefinitionType.Text,
      },
    ],
    [t, isAdminOrTechOperator, categoryOptions, getCategoryLabel],
  );

  const columns = useMemo(
    (): DataGridColumnDefinition<InternalTransfer>[] => [
      {
        header: t('fields.code'),
        valueGetter: (item) => <CopyTextId id={item.id} />,
      },
      {
        header: t('fields.date'),
        valueGetter: (item) => (
          <DateLabel>{formatUtils.formatDate(item.createdAt)}</DateLabel>
        ),
      },
      {
        header: t('fields.amount'),
        valueGetter: (item) => (
          <Money
            value={item.amount}
            assetCurrencyId={item.senderAsset?.assetCurrencyId}
            symbol
          />
        ),
      },
      {
        header: t('fields.details'),
        valueGetter: (item) => {
          if (!item.details) {
            return '-';
          }
          return t(`details.${item.details}`);
        },
      },
      {
        header: t('fields.status'),
        valueGetter: (item) => {
          let status = 'active';
          if (item.status === InternalTransferStatus.Active) {
            status = 'inactive';
          } else if (item.status === InternalTransferStatus.Cancelled) {
            status = 'error';
          }

          return (
            <StatusLabel
              label={t(`status.${item.status}`)}
              status={status as any}
            ></StatusLabel>
          );
        },
      },
      {
        header: t('fields.sender'),
        valueGetter: (item) => {
          const shopName = find(queryResultShops?.data, {
            id: item.senderAsset?.shopId,
          })?.name;
          const p2pProviderName = item.senderAsset?.p2pProviderId
            ? find(p2pProviders, { id: item.senderAsset?.p2pProviderId })?.name
            : null;

          return (
            <Fragment>
              <div>{item.senderUser.name}</div>
              {shopName && <div>{shopName}</div>}
              {p2pProviderName && <div>{p2pProviderName}</div>}
            </Fragment>
          );
        },
      },
      {
        header: t('fields.category'),
        valueClassName: 'tw-min-w-[200px]',
        valueGetter: (item) => (
          <div>
            {item.category && (
              <Chip
                label={getCategoryLabel(item.category)}
                color="secondary"
                variant="outlined"
                size="small"
              />
            )}
            {item.comment && (
              <div className="tw-text-secondary tw-text-gray-500 tw-italic tw-p-1">
                {item.comment}
              </div>
            )}
          </div>
        ),
        hidden: !isAdminOrTechOperator,
      },
      ...(additionalColumns ? additionalColumns : []),
    ],
    [
      t,
      additionalColumns,
      queryResultShops,
      p2pProviders,
      isAdminOrTechOperator,
      getCategoryLabel,
    ],
  );

  const filtersDefinitions = useMemo(
    () =>
      concat(
        filtersUtils.getCommonFilters(filters),
        commonFilters,
        additionalFilters,
      ),
    [additionalFilters, commonFilters, filters],
  );

  return (
    <CrudPage
      filters={{ filtersDefinitions }}
      table={{ queryResult, columns, paginated: true }}
    />
  );
};
